'use client';

import cx from 'classnames';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { ReactNode, memo } from 'react';

import XMarkIcon from '@flipster/react/icon/generated/XMarkIcon';
import { useBodyClass } from '@flipster/react/misc/browser/body-class';
import { Portal } from '@flipster/react/misc/portal';

import styles from './modal.module.scss';

interface Props {
  isShow: boolean;
  size: 'small' | 'large';
  className?: string;
  header?: ReactNode;
  children: ReactNode;
  withFooter?: boolean;
  onClose?: () => void;
  noPadding?: boolean;
  noScroll?: boolean;
  image?: string;
}

export const Modal = memo(
  ({ isShow, size, className, header, withFooter, children, onClose, noPadding, noScroll, image }: Props) => {
    useBodyClass('modal', isShow);

    return (
      <Portal selector='#modal'>
        <AnimatePresence initial={false}>
          {isShow && (
            <aside className={styles.wrapper} translate='no'>
              <m.div
                className={cx(styles.modal, styles[size], className, { [styles.imageModal]: !!image })}
                initial={{ opacity: 0.5, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1, transition: { ease: 'easeOut', duration: 0.2 } }}
                exit={{ opacity: 0.5, scale: 0.8, transition: { ease: 'easeIn', duration: 0.2 } }}
              >
                <header className={styles.header}>
                  {onClose && (
                    <button type='button' aria-label='close' className={styles.closeButton} onClick={onClose}>
                      <XMarkIcon size={24} />
                    </button>
                  )}
                  {image && <img src={image} alt='banner' className={styles.bannerImage} />}
                  {header && (typeof header === 'string' ? <h2>{header}</h2> : header)}
                </header>
                <div
                  className={cx(styles.contents, {
                    [styles.withFooter]: withFooter,
                    [styles.noPadding]: noPadding,
                    [styles.noScroll]: noScroll,
                  })}
                >
                  {children}
                </div>
              </m.div>
            </aside>
          )}
        </AnimatePresence>
      </Portal>
    );
  },
);
